import {
  composeRenderProps,
  Button as RACButton,
  ButtonProps as RACButtonProps,
} from "react-aria-components";
import { FunctionComponent, ReactNode, SVGProps } from "react";
import { cn } from "@/utils/helpers";
import { Link } from "react-router";
import {
  KIT_BUTTON_ICON_CLASS,
  KitButtonSpinner,
  kitButtonClass,
  type KitButtonSize,
  type KitButtonVariant,
} from "@/app/routes/dashboard/components/kit/Button";

export type IconPosition = "left" | "right";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "error";

export type ButtonStyle = "filled" | "outline" | "link";

export type ButtonSize = "small" | "medium" | "large";

export interface ButtonProps extends RACButtonProps {
  /** @default 'primary' */
  variant?: ButtonVariant;
  /** @default 'filled' */
  buttonStyle?: ButtonStyle;
  loading?: boolean;
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  iconPosition?: IconPosition;
  iconClassName?: string;
  size?: ButtonSize;
  href?: string;
}

/*
 * The react-aria Button (onPress) for the calendar, the client portal and Live.
 * It draws the library Button through kitButtonClass, so there is one button
 * look (CreativeOS Library.lib.pen, style B): capsules, Primary the solid
 * accent, Secondary control glass, Ghost accent text, Danger solid. Style B has
 * no outlined buttons: "outline" reads as Secondary, a quiet destructive as
 * alert ink with no box, and "link" as a Text action. small and medium are the
 * standard 32px; large is the 44px touch size.
 */
function resolveVariant(variant: ButtonVariant, style: ButtonStyle): KitButtonVariant {
  if (variant === "error") return style === "outline" ? "ghost" : "danger";
  if (variant === "ghost") return "ghost";
  if (style === "outline") return "secondary";
  return variant;
}

const LINK_TONE: Record<ButtonVariant, string> = {
  primary: "text-co-accent",
  secondary: "text-co-muted hover:text-co-text",
  ghost: "text-co-muted hover:text-co-text",
  error: "text-co-alert-ink",
};

function buttonClass({
  variant,
  style,
  size,
  iconOnly,
}: {
  variant: ButtonVariant;
  style: ButtonStyle;
  size: KitButtonSize;
  iconOnly: boolean;
}) {
  const focus =
    "outline-none data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-co-accent forced-colors:data-[focus-visible]:outline-[Highlight]";
  if (style === "link") {
    return cn(
      "unstyled-link inline-flex items-center gap-1.5 bg-transparent p-0 text-[13px] font-medium underline-offset-4 hover:underline",
      "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
      LINK_TONE[variant],
      focus,
    );
  }
  return cn(
    "unstyled-link",
    kitButtonClass({ variant: resolveVariant(variant, style), size, iconOnly }),
    variant === "error" && style === "outline" && "text-co-alert-ink not-disabled:hover:bg-[color-mix(in_srgb,var(--co-danger)_10%,transparent)]",
    focus,
  );
}

const ButtonIcon = ({
  icon: Icon,
  size,
  className,
}: {
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  size: KitButtonSize;
  className?: string;
}) => <Icon aria-hidden className={cn(KIT_BUTTON_ICON_CLASS[size], className)} />;

export function Button({
  children,
  loading,
  variant = "primary",
  buttonStyle = "filled",
  icon: Icon,
  iconPosition = "left",
  iconClassName,
  className,
  isPending,
  size = "medium",
  href,
  ...props
}: ButtonProps) {
  const pending = loading || isPending;
  const kitSize: KitButtonSize = size === "large" ? "lg" : "sm";
  const iconOnly = Boolean(Icon) && (children == null || children === "");

  if (href) {
    const resolvedChildren =
      typeof children === "function"
        ? (children as (props: { isPending?: boolean }) => ReactNode)({})
        : children;
    return (
      <Link
        to={href}
        className={cn(
          buttonClass({ variant, style: buttonStyle, size: kitSize, iconOnly }),
          typeof className === "function" ? undefined : className,
        )}
      >
        {Icon && iconPosition === "left" && <ButtonIcon icon={Icon} size={kitSize} className={iconClassName} />}
        {resolvedChildren}
        {Icon && iconPosition === "right" && <ButtonIcon icon={Icon} size={kitSize} className={iconClassName} />}
      </Link>
    );
  }

  return (
    <RACButton
      {...props}
      isDisabled={pending || props.isDisabled}
      isPending={pending}
      className={composeRenderProps(className, (cls) =>
        cn(buttonClass({ variant, style: buttonStyle, size: kitSize, iconOnly }), cls),
      )}
    >
      {composeRenderProps(children, (kids, { isPending: busy }) => (
        <>
          <span className={cn("inline-flex items-center justify-center gap-[inherit]", busy && "invisible")}>
            {Icon && iconPosition === "left" && <ButtonIcon icon={Icon} size={kitSize} className={iconClassName} />}
            {kids}
            {Icon && iconPosition === "right" && <ButtonIcon icon={Icon} size={kitSize} className={iconClassName} />}
          </span>
          {busy && (
            <span aria-hidden className="absolute inset-0 z-10 flex items-center justify-center">
              <KitButtonSpinner />
            </span>
          )}
        </>
      ))}
    </RACButton>
  );
}

export default Button;
